import { franc } from 'franc-min'
import type { Database } from 'types/supabase'
import type { EventFormEvent } from 'components/events/EventForm/types'
import { mergeVenueHistory, pickDeskLocation } from './venueHistory'
import type { VenueHistoryRow } from './venueHistory'
import { buildVenueIndex, matchVenueLayerA, venueNameKey } from './venueNameKey'
import type { VenueAliasRow, VenueKeyRow } from './venueNameKey'

export { venueNameKey } from './venueNameKey'

/**
 * The desk form's pre-fill (D-06, DESK-07).
 *
 * ApplicationEdit (the Jotform / legacy path, D-01, unchanged) fills venue and
 * city from free text and sets `organizer_id: null`. The editor already
 * resolved those: it writes `venue_id`, `city_id`, `organizer_id` and
 * `category`. The desk uses them, so Dominique does not re-pick venue and
 * organizer on every candidate and DESK-07's metric measures the editor, not
 * the form:
 *
 * - `venue_id` found in `venues` -> `venue` = its id and `venues` = the row, so
 *   the form opens with that venue selected; name/address/description and
 *   lat/long come from the row.
 * - `venue_id` null -> the desk looks the editor's venue text up itself
 *   (`matchVenueByName`) with the same layer-A matcher the editor uses
 *   (Phase 33.4 D-05 / D-08, `matchVenueLayerA`), over the town's venues and
 *   their aliases (`venueAliases`): exactly one venue of the candidate's city
 *   whose name or alias is the same once accents, punctuation and small linking
 *   words are ignored ("Centro de Artes de Sines" = "Centro Artes de Sines"),
 *   whole or by one part of the text ("CAS – Auditório" with the alias CAS), is
 *   selected, as if the editor had resolved it. A listed venue always beats the
 *   free text. The desk never calls a model: the editor's layer B suggestion
 *   arrives as `venue_id` with `venue_match = 'model'`.
 * - no venue found either way -> no `venues`, the form opens on the
 *   custom venue with the free text (`event_venue_location`,
 *   `venue_location_address`, `venue_village`, `venue_description`), the same
 *   columns ApplicationEdit's extractVenueInfo reads.
 * - custom venue -> location from `google_maps` coordinates (plain, `@lat,lng`,
 *   `?q=lat,lng`; short links ignored), else the most common pin of earlier
 *   custom-venue events with the same name key in the same town
 *   (`venueHistory`), else empty (spec-desk-venue-location D-03). History also
 *   fills an empty address or description.
 * - `city_id` -> `city_id`, and `venue_city` is that cities row's name.
 * - `organizer_id` -> `organizer_id` (ApplicationEdit's is always null).
 * - `category` -> `main_tag` by ApplicationEdit's tag match, case-insensitive.
 *
 * Three rules are restated from `ApplicationEdit.constructEventFromApplication`
 * (its twin; ApplicationEdit is not touched by the desk):
 * 1. language split: franc on the description, then the title, default
 *    English; a Portuguese post fills only the `_pt` side. Desk only (UAT
 *    33.1): under an English description the title and subtitle keep their
 *    own language (deskSourceSides);
 * 2. tag match: `"{tag} / {tag_pt}"`, `tag` or `tag_pt` equals the category;
 * 3. FBIMP-12, desk variant: a date the post states is filled even when its
 *    time is not (an exhibition with opening hours has no single time). The
 *    missing time is a visible placeholder, 00:00 for the start and 23:59 for
 *    the end (so the last day is not cut off), and `timeNotStated` names the
 *    pickers the candidate screen flags with "time not stated". The end
 *    placeholder needs a stated end date: a post that names no end at all
 *    still opens with an empty end. No date, no datetime, as before.
 *
 * Pure: no Supabase, no React, no i18n. `publicUrl` is injected (the caller
 * passes storage.from('events').getPublicUrl(path).data.publicUrl).
 */

export type ApplicationRow = Database['public']['Tables']['applications']['Row']
export type VenueRow = Database['public']['Tables']['venues']['Row']
export type TagRow = Database['public']['Tables']['tags']['Row']
export type CityRow = Database['public']['Tables']['cities']['Row']

/** The raw item's stored images, in stored order (image 1, then the extras). */
export type DeskRawImages = {
  image_path: string | null
  extra_image_paths: string[] | null
}

/** A tag as the desk receives it: a tags Row, or EventForm's narrower tag. */
export type DeskTag = Pick<TagRow, 'id'> & {
  tag: string | null
  tag_pt?: string | null
}

export type DeskPrefillDeps = {
  venues: VenueRow[]
  tags: DeskTag[]
  cities: CityRow[]
  publicUrl: (path: string) => string
  rawItem: DeskRawImages
  /**
   * Earlier custom-venue events in the candidate's town, grouped by
   * custom_venue_history (0038, D-02). Optional: callers without a history
   * lookup (the update proposal overlay) omit it and get the google_maps step
   * only. The desk passes [] when there is no town, no custom venue, or the
   * lookup failed (D-10).
   */
  venueHistory?: VenueHistoryRow[]
  /**
   * The listed venues' aliases (`venue_aliases`, Phase 33.4 D-08). Optional:
   * callers without the alias lookup (the update proposal overlay) omit it and
   * match on venue names only. The desk passes [] while they load or when the
   * lookup failed (never-throw hook).
   */
  venueAliases?: VenueAliasRow[]
}

/**
 * The exact `weekly_event` literal. Twin of ApplicationEdit's `weekly` prop and
 * of the editor's WEEKLY_EVENT_YES (mobile/supabase/functions/_shared/editor/
 * extraction.ts), which writes this string when the post repeats weekly.
 */
export const WEEKLY_EVENT_YES = 'Yes - for this month / Sim - para este mês'

/** The weekly flag the form opens with, as ApplicationEdit derives it. */
export const deskWeekly = (application: ApplicationRow): boolean =>
  application.weekly_event === WEEKLY_EVENT_YES

/**
 * The storage path of the editor's pick (D-08): `display_image_index` 1 is
 * `image_path`, n is `extra_image_paths[n - 2]` (0035's 1-based
 * `v_extra[p_index - 1]`). A null index means image 1. The desk's own swap
 * (`desk_display_image_index`) is deliberately ignored: this is the editor's
 * pick, the baseline a swap is measured against.
 */
export const editorPickPath = (
  application: ApplicationRow,
  rawItem: DeskRawImages
): string | null => {
  const index = application.display_image_index ?? 1
  if (index === 1) return rawItem.image_path ?? null
  if (index < 1) return null
  return rawItem.extra_image_paths?.[index - 2] ?? null
}

// Twin of ApplicationEdit.detectLanguage.
const detectLanguage = (text: string): 'portuguese' | 'english' | null => {
  if (!text || text.trim().length < 10) return null
  const langCode = franc(text)
  if (langCode === 'por') return 'portuguese'
  if (langCode === 'eng') return 'english'
  return null
}

/**
 * The side buildDeskPrefill puts the original title, subtitle, description and
 * price on: the description's language, else the title's, else English. The
 * 33.1 duplicate re-check sends that side's title (allowed now that 33.3 has
 * merged, D-31), so both read this one rule.
 */
export const deskSourceIsPortuguese = (
  application: Pick<ApplicationRow, 'event_description' | 'event_title'>
): boolean => {
  const language =
    detectLanguage(application.event_description || '') ??
    detectLanguage(application.event_title || '') ??
    'english'
  return language === 'portuguese'
}

/**
 * A short text read as Portuguese or English only (franc alone calls most
 * titles "too short" or another language). Used for the title and subtitle
 * under an English description, where a wrong "Portuguese" costs nothing:
 * the field still gets both sides, the original on one and DeepL's on the other.
 */
const readsPortuguese = (text: string | null | undefined): boolean =>
  Boolean(text && text.trim()) && franc(text as string, { only: ['por', 'eng'], minLength: 3 }) === 'por'

/** true = the field's original goes on the `_pt` side. */
export interface DeskSourceSides {
  /** description and price: deskSourceIsPortuguese */
  body: boolean
  title: boolean
  subtitle: boolean
}

/**
 * UAT 33.1 (2026-11-03, application 2565): the editor can write an English
 * description for a Portuguese post ("InclusivaMENTE: Oficina de Psicologia
 * Positiva"), and the one-side rule put that Portuguese title on the English
 * side. Under an English description the title and subtitle keep their own
 * language. Under a Portuguese description every field stays on the pt side,
 * as before: an English-looking title there is a name ("CHI KUNG", "Sarau
 * Latino"), and reading it alone would move it to the wrong side.
 */
export const deskSourceSides = (
  application: Pick<ApplicationRow, 'event_description' | 'event_title' | 'subtitle'>
): DeskSourceSides => {
  const body = deskSourceIsPortuguese(application)
  if (body) return { body, title: true, subtitle: true }
  return {
    body,
    title: readsPortuguese(application.event_title),
    subtitle: readsPortuguese(application.subtitle)
  }
}

const lower = (value: string | null | undefined) => (value ?? '').trim().toLowerCase()

// Twin of ApplicationEdit's tag match, compared case-insensitively.
const matchMainTag = (category: string | null, tags: DeskTag[]): number | null => {
  const wanted = lower(category)
  if (!wanted) return null
  const found = tags.find((tag) =>
    (tag.tag && tag.tag_pt && lower(`${tag.tag} / ${tag.tag_pt}`) === wanted) ||
    (!!tag.tag && lower(tag.tag) === wanted) ||
    (!!tag.tag_pt && lower(tag.tag_pt) === wanted)
  )
  return found ? found.id : null
}

/** The placeholder times of a stated date whose time the post does not state. */
export const START_TIME_PLACEHOLDER = '00:00:00'
export const END_TIME_PLACEHOLDER = '23:59:00'

/**
 * The date pickers that open on a placeholder time: the date is stated, the
 * time is not (for the end, the end date itself must be stated). The candidate screen flags these so 00:00 / 23:59 are never
 * approved by accident.
 */
export const timeNotStated = (
  application: Pick<ApplicationRow, 'starting_date' | 'starting_time' | 'ending_date' | 'ending_time'>
): { start: boolean; end: boolean } => ({
  start: !!application.starting_date && !application.starting_time,
  end: !!application.ending_date && !application.ending_time
})

/**
 * The listed venues of one town (`city_id`) whose `venueNameKey` equals the
 * name's. No town or no key -> none. The Save-as-venue duplicate guard
 * (33.3 CR-01) uses it. Phase 33.4 (P-11): the
 * listed-venue match moved to layer A (`matchVenueByName`); this keeps its
 * name-only signature, and the desk additionally treats a text that resolves
 * by alias as a listed twin.
 */
export const listedVenuesNamed = <V extends Pick<VenueRow, 'city_id' | 'name'>>(
  venues: V[],
  cityId: number | null,
  name: string | null | undefined
): V[] => {
  const key = venueNameKey(name)
  if (cityId === null || !key) return []
  return venues.filter((venue) => venue.city_id === cityId && venueNameKey(venue.name) === key)
}

/** At most this many "Use <venue>" suggestions are shown. */
export const RESEMBLING_VENUES_MAX = 3

/**
 * The listed venues of one town whose name is the typed name with more words
 * after it, or the other way round, compared on whole words of the
 * `venueNameKey` ("Cine Granadeiro" and "Cine Granadeiro - Auditório
 * Municipal"; "Cine Granadeiro" never resembles "Cine Grand"). Exact matches
 * are left out: those are `listedVenuesNamed`. No town or no key -> none.
 *
 * UAT 2026-11-02: the exact-name guard (CR-01) let "Save as venue" offer a
 * second Cine Granadeiro because the listed one carries a longer name. The
 * desk shows these as "Use <venue>" instead of offering to create one.
 */
export const listedVenuesResembling = <V extends Pick<VenueRow, 'city_id' | 'name'>>(
  venues: V[],
  cityId: number | null,
  name: string | null | undefined
): V[] => {
  const key = venueNameKey(name)
  if (cityId === null || !key) return []
  return venues.filter((venue) => {
    if (venue.city_id !== cityId) return false
    const other = venueNameKey(venue.name)
    if (!other || other === key) return false
    return other.startsWith(`${key} `) || key.startsWith(`${other} `)
  })
}

/**
 * The listed venue the editor's venue text names, when the editor left
 * `venue_id` empty: layer A (Phase 33.4 D-05, D-08), the same matcher the
 * editor runs, over the town's venue names and `aliases`. No city, no text, no
 * match or several matches -> none, so a same-name venue in another town is
 * never picked (the editor's T-31-17 rule). Never a model.
 */
export const matchVenueByName = (
  application: Pick<ApplicationRow, 'city_id' | 'event_venue_location'>,
  venues: VenueRow[],
  aliases: VenueAliasRow[] = []
): VenueRow | undefined => {
  const match = matchVenueLayerA(application.event_venue_location, application.city_id, buildVenueIndex(venueKeyRows(venues), aliases))
  return match === null ? undefined : venues.find((venue) => venue.id === match.venueId)
}

/**
 * Venue rows as the layer-A index takes them: `venues.name` is nullable in the
 * generated types, and a nameless venue has an empty key, which the index skips.
 */
export const venueKeyRows = (venues: readonly VenueRow[]): VenueKeyRow[] =>
  venues.map((venue) => ({ id: venue.id, name: venue.name ?? '', city_id: venue.city_id }))

export type DeskPrefillOptions = {
  /**
   * Look the venue up by name when the editor left `venue_id` empty (default
   * on). The edited-fields baseline turns it off: DESK-07 measures what the
   * editor resolved, so a venue only the desk found still counts as hers.
   */
  matchVenue?: boolean
}

/**
 * The listed venue the form opens on (D-03 step 1): the editor's `venue_id`,
 * else the desk's own name lookup (`matchVenueByName`, unless turned off).
 * Undefined -> the custom-venue path. Exported so the desk knows the path
 * before the history lookup returns.
 */
export const resolveDeskVenue = (
  application: ApplicationRow,
  venues: VenueRow[],
  { matchVenue = true }: DeskPrefillOptions = {},
  aliases: VenueAliasRow[] = []
): VenueRow | undefined =>
  application.venue_id === null
    ? (matchVenue ? matchVenueByName(application, venues, aliases) : undefined)
    : venues.find((venue) => venue.id === application.venue_id)

const customVenueLocation = (
  application: ApplicationRow,
  venueHistory: VenueHistoryRow[]
) => {
  const history = mergeVenueHistory(venueHistory, application.event_venue_location)
  const pick = pickDeskLocation(application.google_maps, history)
  const [lat, lng] = pick.location ? pick.location.split(',').map(Number) : [null, null]
  return { history, lat, lng }
}

export const buildDeskPrefill = (
  application: ApplicationRow,
  deps: DeskPrefillDeps,
  options: DeskPrefillOptions = {}
): EventFormEvent => {
  const sides = deskSourceSides(application)
  const enSide = (value: string | null, isPortuguese = sides.body) => (isPortuguese ? '' : value || '')
  const ptSide = (value: string | null, isPortuguese = sides.body) => (isPortuguese ? value || '' : '')

  // FBIMP-12, desk variant: a stated date is filled, a missing time is a
  // flagged placeholder (timeNotStated).
  const endDate = application.ending_date || application.starting_date
  const startTime = application.starting_date
    ? `${application.starting_date}T${application.starting_time ?? START_TIME_PLACEHOLDER}+00:00`
    : null
  const finishTime = endDate && application.ending_time
    ? `${endDate}T${application.ending_time}+00:00`
    : application.ending_date
      ? `${application.ending_date}T${END_TIME_PLACEHOLDER}+00:00`
      : null

  const venueRow = resolveDeskVenue(application, deps.venues, options, deps.venueAliases ?? [])
  const cityRow =
    application.city_id === null
      ? undefined
      : deps.cities.find((city) => city.id === application.city_id)

  const pickPath = editorPickPath(application, deps.rawItem)
  const picture = application.main_picture || (pickPath ? deps.publicUrl(pickPath) : '')

  const price = application.price === '0' ? null : application.price

  const custom = venueRow ? null : customVenueLocation(application, deps.venueHistory ?? [])

  const venueFields = venueRow
    ? {
        venue: venueRow.id,
        venues: venueRow,
        venue_name: venueRow.name || '',
        venue_address: venueRow.address || '',
        venue_city: cityRow?.name || venueRow.city || '',
        venue_description: venueRow.description || '',
        venue_location_lat: venueRow.location_lat,
        venue_location_long: venueRow.location_long
      }
    : {
        venue: null,
        venue_name: application.event_venue_location || '',
        venue_address: application.venue_location_address || custom?.history?.address || '',
        venue_city: cityRow?.name || application.venue_village || '',
        venue_description: application.venue_description || custom?.history?.description || '',
        venue_location_lat: custom?.lat ?? null,
        venue_location_long: custom?.lng ?? null
      }

  return {
    title: enSide(application.event_title, sides.title),
    title_pt: ptSide(application.event_title, sides.title),
    sub_title: enSide(application.subtitle, sides.subtitle),
    sub_title_pt: ptSide(application.subtitle, sides.subtitle),
    description: enSide(application.event_description),
    description_pt: ptSide(application.event_description),
    picture,
    start_time: startTime,
    finish_time: finishTime,
    price: enSide(price),
    price_pt: ptSide(price),
    organizer_id: application.organizer_id,
    main_tag: matchMainTag(application.category, deps.tags),
    city_id: application.city_id,
    ...venueFields,
    published: true,
    cancelled: false,
    is_recommended: false,
    registration_link: ''
  }
}
